import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Building2,
  Eye,
  EyeOff,
  Lock,
  User,
  ShieldCheck,
  Users,
  BarChart3,
} from "lucide-react";
import toast from "react-hot-toast";
import slide1 from "../assets/login/slide-1.jpg";
import slide2 from "../assets/login/slide-2.jpg";
import slide3 from "../assets/login/slide-3.jpg";
import slide4 from "../assets/login/slide-4.jpg";

const highlights = [
  {
    icon: Users,
    title: "Resident management",
    description: "Approve registrations, manage units, and track occupancy.",
  },
  {
    icon: ShieldCheck,
    title: "Access control",
    description: "Oversee visitors, security staff, and estate access logs.",
  },
  {
    icon: BarChart3,
    title: "Estate insights",
    description: "Monitor activity across every estate from one dashboard.",
  },
];

const slides = [slide1, slide2, slide3, slide4];
const SLIDE_INTERVAL_MS = 5000;

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);

  const { login } = useAuth();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      const result = await login(username, password);
      if (result.success) {
        navigate("/");
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Branding panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-950">
        {slides.map((slide, index) => (
          <div
            key={slide}
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{ opacity: index === slideIndex ? 1 : 0 }}
          >
            <img
              src={slide}
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-indigo-950/75 to-slate-950/60" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/15">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <span className="text-base font-semibold tracking-tight text-white">
              Estate Manage
            </span>
          </div>

          <div className="max-w-md">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-indigo-300 mb-5">
              Admin Console
            </span>
            <h1 className="text-4xl font-bold leading-[1.15] mb-4 tracking-tight text-white">
              Run every estate from a single dashboard
            </h1>
            <p className="text-slate-300 text-base leading-relaxed mb-10">
              Sign in to manage residents, visitors, security, and property
              units across all your estates.
            </p>

            <div className="space-y-3">
              {highlights.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="flex items-start gap-3.5 p-3.5 rounded-xl bg-white/[0.04] border border-white/10"
                >
                  <div className="h-9 w-9 shrink-0 bg-white/10 rounded-lg flex items-center justify-center border border-white/10">
                    <Icon className="h-4 w-4 text-indigo-200" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {title}
                    </p>
                    <p className="text-sm text-slate-400">{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              &copy; {currentYear} Estate Manage &middot; Secure estate
              management system
            </p>
            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide}
                  type="button"
                  aria-label={`Show slide ${index + 1}`}
                  onClick={() => setSlideIndex(index)}
                  className={`h-1.5 rounded-full transition-all ${
                    index === slideIndex
                      ? "w-6 bg-white/80"
                      : "w-1.5 bg-white/25 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Login form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-sm w-full">
          <div className="text-center lg:text-left mb-6 lg:hidden">
            <div className="mx-auto h-14 w-14 bg-gradient-to-br from-indigo-600 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/25 mb-6">
              <Building2 className="h-7 w-7 text-white" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xl shadow-slate-200/60 p-8">
            <div className="mb-7">
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                Welcome back
              </h2>
              <p className="mt-1.5 text-sm text-slate-500">
                Sign in to your admin account to continue
              </p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="username" className="form-label">
                  Username
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="form-input"
                    placeholder="Enter your username"
                    style={{ paddingLeft: "2.5rem" }}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                    placeholder="Enter your password"
                    style={{ paddingLeft: "2.5rem", paddingRight: "2.75rem" }}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn btn-primary py-2.5 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Sign in
                  </>
                )}
              </button>
            </form>

            <div className="mt-7 flex items-center gap-3">
              <div className="flex-1 border-t border-slate-200" />
              <span className="text-xs font-medium text-slate-400">
                Admin access only
              </span>
              <div className="flex-1 border-t border-slate-200" />
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400 lg:hidden">
            &copy; {currentYear} Estate Manage &middot; Secure estate
            management system
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
